import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Mail, Calendar, CheckCircle2, Clock, Download, Copy, Send, Loader2 } from "lucide-react";

function fmt(iso) {
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function RegistrantsPage() {
  const { id } = useParams();
  const [registrants, setRegistrants] = useState(null);
  const [webinar, setWebinar] = useState(null);
  const [sending, setSending] = useState({});

  useEffect(() => {
    api.get(`/admin/webinars/${id}`).then((r) => setWebinar(r.data)).catch(() => {});
    api.get(`/admin/webinars/${id}/registrants`).then((r) => setRegistrants(r.data)).catch(() => setRegistrants([]));
  }, [id]);

  const exportCsv = () => {
    if (!registrants?.length) return;
    const rows = [["Name", "Email", "Session", "Payment", "Registered", "Join link"]];
    registrants.forEach((r) => rows.push([r.name, r.email, r.session_start, r.payment_status, r.created_at, r.join_url || ""]));
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "registrants.csv";
    a.click();
  };

  const copyLink = async (r) => {
    if (!r.join_url) return;
    try {
      await navigator.clipboard.writeText(r.join_url);
      toast.success("Join link copied");
    } catch {
      toast.error("Couldn't copy. Long-press to copy manually.");
    }
  };

  const resend = async (r) => {
    setSending((s) => ({ ...s, [r.id]: true }));
    try {
      await api.post(`/admin/registrants/${r.id}/resend-link`);
      toast.success(`Join link re-sent to ${r.email}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to send");
    }
    setSending((s) => ({ ...s, [r.id]: false }));
  };

  const canJoin = (r) => r.payment_status === "paid" || r.payment_status === "free";

  return (
    <div className="p-8" data-testid="registrants-page">
      <Link to="/admin/webinars" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#4B5563] hover:text-[#6D28B5] mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to webinars
      </Link>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-black tracking-tighter">Registrants</h1>
          <p className="text-[#4B5563] mt-1">{webinar?.title}</p>
        </div>
        <button onClick={exportCsv} className="border border-[#0A0A0A] hover:bg-[#F3F4F6] px-4 py-2.5 font-semibold flex items-center gap-2" data-testid="export-csv-btn">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {registrants === null ? (
        <div className="text-[#9CA3AF]">Loading…</div>
      ) : registrants.length === 0 ? (
        <div className="border border-dashed border-[#9CA3AF] bg-white p-16 text-center">
          <p className="text-[#4B5563] font-medium">No registrants yet.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#0A0A0A]">
          <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-[#E5E7EB] text-xs uppercase tracking-wider font-bold text-[#4B5563]">
            <div className="col-span-3">Attendee</div>
            <div className="col-span-2">Session</div>
            <div className="col-span-2">Payment</div>
            <div className="col-span-2">Registered</div>
            <div className="col-span-3">Join link</div>
          </div>
          {registrants.map((r) => (
            <div key={r.id} className="grid grid-cols-12 gap-4 px-5 py-4 border-b border-[#E5E7EB] last:border-0 text-sm items-center" data-testid={`registrant-${r.id}`}>
              <div className="col-span-3 min-w-0">
                <div className="font-semibold truncate">{r.name}</div>
                <div className="text-xs text-[#4B5563] flex items-center gap-1 truncate"><Mail className="w-3 h-3" /> {r.email}</div>
              </div>
              <div className="col-span-2 text-[#4B5563] flex items-center gap-1.5 text-xs"><Calendar className="w-3.5 h-3.5" /> {fmt(r.session_start)}</div>
              <div className="col-span-2">
                {r.payment_status === "paid" ? (
                  <span className="inline-flex items-center gap-1 text-[#10B981] font-semibold text-xs"><CheckCircle2 className="w-3.5 h-3.5" /> Paid</span>
                ) : r.payment_status === "free" ? (
                  <span className="text-xs font-semibold text-[#4B5563]">Free</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[#F59E0B] font-semibold text-xs"><Clock className="w-3.5 h-3.5" /> Pending</span>
                )}
              </div>
              <div className="col-span-2 text-[#4B5563] text-xs">{fmt(r.created_at)}</div>
              <div className="col-span-3">
                {canJoin(r) ? (
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={r.join_url || ""}
                      onFocus={(e) => e.target.select()}
                      data-testid={`join-url-${r.id}`}
                      className="flex-1 min-w-0 border border-[#E5E7EB] bg-[#F9FAFB] px-2 py-1.5 text-xs text-[#4B5563] truncate focus:outline-none focus:ring-2 focus:ring-[#6D28B5]"
                    />
                    <button onClick={() => copyLink(r)} title="Copy join link" data-testid={`copy-join-${r.id}`}
                      className="p-1.5 border border-[#E5E7EB] hover:bg-[#F3F4F6] text-[#4B5563] shrink-0">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => resend(r)} disabled={sending[r.id]} title="Email this link to the attendee" data-testid={`resend-join-${r.id}`}
                      className="p-1.5 bg-[#6D28B5] hover:bg-[#4A1A7A] disabled:opacity-50 text-white shrink-0 flex items-center gap-1">
                      {sending[r.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                ) : (
                  <span className="text-xs text-[#9CA3AF]">Unpaid, no join link yet</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
